import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { otaPendingReservationService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { EditTable } from '../../../organisms'
import { Button } from '../../../atoms'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import OtaPendingReservationSearch from 'components/organisms/OtaPendingReservations/OtaPendingReservationSearch'
import { IOtaPendingFilterOptions } from 'components/organisms/OtaPendingReservations/OtaPendingReservationFilter'

import * as constants from '../../../../static/constants'
import {
  OTA_PENDING_STATUS_LABELS,
  normalizeOtaPendingStatus,
  getStatusBadgeBackgroundColor,
  getStatusBadgeTextColor,
} from '../../../../static/constants_ota_pending'
import AgentLayoutIndex from '../_layouts'

declare var window: interfaces.IWindow

const DEFAULT_FILTER_OPTIONS: IOtaPendingFilterOptions = {}

interface IProps {
  user: {
    users: IJsonResponse
    meta: interfaces.IPager
  }
}

const COLUMNS = [
  {
    name: I18n.t('generic.id'),
    field: 'id',
    required: false,
    readOnly: true,
    link: record => `/agent/ota_pending_reservations/${record.id}/edit`,
  },
  {
    name: 'ステータス',
    renderElement: record => {
      const statusId = normalizeOtaPendingStatus(record.status)
      const statusLabel = OTA_PENDING_STATUS_LABELS[statusId] || record.status
      return <StatusBadge status={statusId}>{statusLabel}</StatusBadge>
    },
    field: 'status',
    required: false,
  },
  {
    name: 'OTA情報',
    renderElement: record => {
      return (
        <>
          {record.seller}
          <br />
          {record.seller_booking_id}
          <br />
          <small>{record.process_action}</small>
        </>
      )
    },
    field: 'seller',
    required: false,
  },
  {
    name: 'ツアー情報',
    renderElement: record => {
      const dayOfWeek = record.tour_date
        ? ['日', '月', '火', '水', '木', '金', '土'][new Date(record.tour_date).getDay()]
        : ''
      return (
        <>
          {record.tour_product_id}
          <br />
          {record.tour_title}
          <br />
          {record.tour_date && (
            <>
              {record.tour_date} ({dayOfWeek}) {record.tour_start_at}
            </>
          )}
        </>
      )
    },
    field: 'tour_title',
    required: false,
  },
  {
    name: '顧客情報',
    renderElement: record => {
      return (
        <>
          {record.customer_full_name}
          <br />
          {record.customer_email}
          <br />
          <small>
            大人:{record.num_of_adults} / 子供:{record.num_of_children} / 幼児:
            {record.num_of_infants}
          </small>
        </>
      )
    },
    field: 'customer_full_name',
    required: false,
  },
  {
    name: '料金',
    renderElement: record => {
      const currency = record.currency || 'JPY'
      const price = record.total_price?.toLocaleString() || '0'
      return (
        <>
          {currency} {price}
        </>
      )
    },
    field: 'total_price',
    required: false,
  },
]

const AgentOtaPendingReservationIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('手動入力一覧')
  const [otaPendingReservations, setOtaPendingReservations] = React.useState([])
  const { data: user } = utilService.getDataFromJson(props.user)
  const [filterOptions, setFilterOptions] = React.useState(DEFAULT_FILTER_OPTIONS)
  const [pagination, setPagination] = React.useState()
  const [isLoading, setIsLoading] = React.useState(true)

  const searchOtaPendingReservations = React.useCallback(
    async _page => {
      try {
        setIsLoading(true)
        window.scrollTo({ top: 0 })
        const {
          ota_pending_reservations: fetchedReservations,
          pagination: meta,
        } = await otaPendingReservationService.getOtaPendingReservations(filterOptions, _page)
        setOtaPendingReservations(fetchedReservations)
        setPagination(meta)
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    },
    [filterOptions]
  )

  React.useEffect(() => {
    searchOtaPendingReservations(1)
  }, [])

  const handleNewReservation = React.useCallback(() => {
    window.location.href = '/agent/ota_pending_reservations/new'
  }, [])

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <style type="text/css">{`
          @container filter-container (width < 640px) {
            .filterContainer {
              width: 100%;
              max-width: 240px;
            }
          }
        `}</style>
        <AgentLayoutIndex
          hideAgentNav={true}
          title={
            <Heading>
              <div className="headingInner">
                <span>手動入力一覧</span>
                <div className="headingActions">
                  <Button primary={true} handleClick={handleNewReservation}>
                    新規作成
                  </Button>
                  <div className="search">
                    <OtaPendingReservationSearch
                      searchOtaPendingReservations={searchOtaPendingReservations}
                      filterOptions={filterOptions}
                      setFilterOptions={setFilterOptions}
                    />
                  </div>
                </div>
              </div>
            </Heading>
          }
          model={'ota_pending_reservations'}
          main={
            <EditTableWrapper>
              <EditTable
                editable={true}
                columns={COLUMNS}
                records={otaPendingReservations}
                pagination={pagination}
                getEditLink={({ id }) => `/agent/ota_pending_reservations/${id}/edit`}
                loading={isLoading}
                getResourceByPage={searchOtaPendingReservations}
                isCustomResource={true}
              />
            </EditTableWrapper>
          }
        />
      </Wrapper>
    </SidebarLayout>
  )
}

const StatusBadge = styled.span<{ status: number }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background-color: ${({ status }) => getStatusBadgeBackgroundColor(status)};
  color: ${({ status }) => getStatusBadgeTextColor(status)};
`

const Wrapper = styled.div`
  .Index_Main {
    padding: 0 !important;

    .Panel {
      padding: 20px 40px !important;
      overflow: visible;
    }

    .Table_Data {
      padding: 0 8px 0 10px !important;
    }

    .Table_Header {
      white-space: nowrap;
      padding: 12px 8px 12px 10px !important;
    }

    .Table_Header,
    .Table_Data {
      max-width: 500px !important;
      min-width: 50px !important;
    }
  }
`

const Heading = styled.div`
  .headingInner {
    display: flex;
    container-type: inline-size;
    container-name: filter-container;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
  }
  .headingActions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 16px;
  }
  .search {
    .filterContainer {
      margin: 0;
      color: ${constants.COLORS.subText};
      font-size: 12px;
      font-weight: normal;
      width: 400px;
    }
  }
`

const EditTableWrapper = styled.div`
  .Table_Row .Table_Header:first-child,
  .Table_Row .Table_Data:first-child {
    min-width: 40px;
  }
`

export default AgentOtaPendingReservationIndex
